import React from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { ThemeProvider } from '@mui/material'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers'
import moment from 'moment'
import { MuiProps, muiOptions } from '../../../../infrastructure/materialUiThemeProvider'
import { useTimePickerState } from './state'
import { fieldStatuses } from '../../styles'
import { t } from '../../../../infrastructure/i18nextHelper'
import { BaseFieldProps } from '../../fieldProps'
import { customTimePickerTheme } from './theme'

type TimePickerStyleProps = {
    timepicker?
}

type TimePickerProps = {
    time: string | null
    setTime: (newTime: string | null) => void
    auto?: boolean
    errormessage?: string
    maxTime?: string
    label: string
    disableFuture?: boolean
    disableNewStyle?: boolean
    hideUnderline?: boolean
    smallIcon?: boolean
    onLostFocus?: (e) => void
    classesOverride?: TimePickerStyleProps
    size?: 'small' | 'standard'
} & BaseFieldProps & MuiProps

function _TimePicker({
    classes,
    classesOverride,
    time,
    onLostFocus,
    setTime,
    disableFuture,
    auto,
    status,
    onError,
    disableNewStyle,
    hideUnderline,
    smallIcon,
    ...props
}: TimePickerProps) {
    let state = useTimePickerState(time, setTime, onError)

    let highlight = status === 'info' ? classes.fieldStatusInfo
        : status === 'warning' ? classes.fieldStatusWarning : ''

    let sizeClass = props.size === 'standard' ? classes.standard : props.size === 'small' ? classes.small : ''

    return (
        <ThemeProvider theme={customTimePickerTheme}>
            <TimePicker
                value={time ? moment(time) : null}
                onChange={state.onChange}
                viewRenderers={{
                    hours: renderTimeViewClock,
                    minutes: renderTimeViewClock,
                    seconds: renderTimeViewClock,
                }}
                format="HH:mm"
                maxTime={props.maxTime ? moment(props.maxTime, 'HH:mm') : undefined}
                disableFuture={!!disableFuture}
                slotProps={{
                    textField: {
                        className: `${classesOverride ? classesOverride.timepicker : classes.picker} ${highlight} ${sizeClass}`,
                        error: status === 'alert' || state.isOnError,
                        label: props.label,
                        placeholder: props.disabled ? '' : 'HH:mm',
                        InputLabelProps: { shrink: true },
                        InputProps: {
                            // @ts-ignore
                            disableunderline: (hideUnderline || props.disabled).toString(),
                        },
                        helperText:
                            status === 'alert' || onError
                                ? props.errormessage || t('components.error')
                                : auto ? t('components.autoCalculated') : null,
                        onBlur: onLostFocus,
                        size: props.size === 'small' ? 'small' : 'medium',
                        disabled: props.disabled,
                        variant: !disableNewStyle ? 'filled' : 'standard'
                    },
                    actionBar: { actions: ['clear', 'cancel', 'accept'] },
                    toolbar: {
                        hidden: false,
                    },
                }}
                minutesStep={5}
                disabled={props.disabled}
                {...props}
            />
        </ThemeProvider>
    )
}

let styles = _ =>
    createStyles({
        small: { width: '8em !important' },
        standard: { width: '10.3em !important' },
        picker: {
            width: '15.3em',
            margin: '0em 0.5em',
            "& .Mui-disabled": {
                backgroundColor: 'white',
                "& span": {
                    color: 'white'
                }
            }
        },
        ...fieldStatuses
    })

export let CustomTimePicker = withStyles(styles, muiOptions)(_TimePicker)
